// pages/AdminLoginLogs.js - Admin Login Logs (matches Dashboard theme: light + saffron accents)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    ShieldCheck, RefreshCw, Search, Calendar, Clock,
    ChevronLeft, ChevronRight, AlertCircle, Inbox,
    UserCog, X
} from 'lucide-react';
import { API_BASE } from '../config/api';

// ── Config ───────────────────────────────────────────────────────────────────
// Adjust to your env setup (e.g. process.env.REACT_APP_API_BASE_URL)
const LOGS_ENDPOINT = `${API_BASE}/api/super-admin/get-logs`;
const PAGE_SIZE = 8;

// Pulls the bearer token from wherever your app stores it post-login.
// Swap this out for your auth context / redux store / etc.
const getAuthToken = () => sessionStorage.getItem('adminToken') || '';

// ── Reusable Panel (from Dashboard) ────────────────────────────────────────
const Panel = ({ children, className = '' }) => (
    <div
        className={`rounded-2xl p-4 sm:p-5 transition-all bg-white ${className}`}
        style={{
            border: '1px solid rgba(255,125,56,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        }}
    >
        {children}
    </div>
);

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatRelativeTime = (isoString) => {
    if (!isoString) return '—';
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
};

const formatDateLabel = (dateStr) => {
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return dateStr;
    }
};

const maskId = (id) => {
    if (!id) return '—';
    return `${id.slice(0, 6)}…${id.slice(-4)}`;
};

// ── Skeleton Row ─────────────────────────────────────────────────────────────
const SkeletonRow = () => (
    <div className="flex items-center gap-3 p-3 rounded-xl animate-pulse">
        <div className="w-9 h-9 rounded-xl bg-orange-50 shrink-0" />
        <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-gray-100" />
            <div className="h-2.5 w-1/4 rounded bg-gray-100" />
        </div>
        <div className="h-5 w-16 rounded-full bg-gray-100" />
    </div>
);

// ── Log Row ──────────────────────────────────────────────────────────────────
const LogRow = ({ log, isLatest }) => (
    <div
        className="flex items-center gap-3 p-3 rounded-xl transition-all bg-white"
        style={{ border: '1px solid transparent' }}
        onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,125,56,0.06)';
            e.currentTarget.style.borderColor = 'rgba(255,125,56,0.2)';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = 'transparent';
        }}
    >
        <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{
                background: isLatest ? 'linear-gradient(135deg, #FF7D38, #FF6B1A)' : 'rgba(255,125,56,0.08)',
                boxShadow: isLatest ? '0 0 14px rgba(255,125,56,0.35)' : 'none',
            }}
        >
            <ShieldCheck size={15} className={isLatest ? 'text-white' : 'text-orange-500'} />
        </div>

        <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
                <p className="text-xs sm:text-sm font-semibold text-gray-800">
                    {log.adminName}
                </p>
                {isLatest && (
                    <span className="text-[9px] sm:text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap"
                        style={{ background: 'rgba(255,125,56,0.15)', color: '#FF7D38' }}>
                        Latest
                    </span>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] sm:text-xs text-gray-500 mt-0.5">
                <span className="flex items-center gap-1">
                    <Calendar size={11} /> {formatDateLabel(log.date)}
                </span>
                <span className="flex items-center gap-1">
                    <Clock size={11} /> {log.time}
                </span>
                <span className="flex items-center gap-1">
                    <UserCog size={11} /> Super Admin
                </span>
            </div>
        </div>

        <span className="text-[10px] sm:text-xs text-gray-400 whitespace-nowrap shrink-0">
            {formatRelativeTime(log.createdAt)}
        </span>
    </div>
);

// ── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3 bg-orange-50">
            <Inbox size={20} className="text-orange-400" />
        </div>
        <p className="text-sm font-medium text-gray-600">{message}</p>
        <p className="text-xs text-gray-400 mt-1">Login activity will appear here once available.</p>
    </div>
);

// ── Error State ────────────────────────────────────────────────────────────
const ErrorState = ({ message, onRetry }) => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3 bg-red-50">
            <AlertCircle size={20} className="text-red-500" />
        </div>
        <p className="text-sm font-medium text-gray-700">Couldn't load login logs</p>
        <p className="text-xs text-gray-400 mt-1 max-w-xs">{message}</p>
        <button
            onClick={onRetry}
            className="mt-4 flex items-center gap-1.5 text-xs font-medium text-white px-4 py-2 rounded-xl transition-all"
            style={{ background: 'linear-gradient(135deg, #FF7D38, #FF6B1A)', boxShadow: '0 0 16px rgba(255,125,56,0.3)' }}
        >
            <RefreshCw size={13} /> Try Again
        </button>
    </div>
);

// ── Main Component ─────────────────────────────────────────────────────────
const AdminLoginLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [page, setPage] = useState(1);

    const fetchLogs = useCallback(async (isRefresh = false) => {
        isRefresh ? setRefreshing(true) : setLoading(true);
        setError(null);
        try {
            const token = getAuthToken();
            const res = await fetch(LOGS_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!res.ok) {
                throw new Error(`Server responded with ${res.status}`);
            }

            const data = await res.json();

            if (!data.success) {
                throw new Error(data.message || 'Failed to fetch logs');
            }

            // Sort newest first, just in case the API doesn't guarantee order
            const sorted = [...(data.logs || [])].sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
            setLogs(sorted);
            setPage(1);
        } catch (err) {
            setError(err.message || 'Something went wrong while fetching logs.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const filteredLogs = useMemo(() => {
        let result = logs;

        if (dateFilter) {
            result = result.filter((log) => log.date === dateFilter);
        }

        if (search.trim()) {
            const q = search.trim().toLowerCase();
            result = result.filter(
                (log) =>
                    log.date?.toLowerCase().includes(q) ||
                    log.time?.toLowerCase().includes(q) ||
                    log.adminId?.toLowerCase().includes(q) ||
                    log.adminName?.toLowerCase().includes(q)
            );
        }

        return result;
    }, [logs, search, dateFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
    const paginatedLogs = filteredLogs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const todayCount = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return logs.filter((l) => l.date === today).length;
    }, [logs]);

    const hasActiveFilters = Boolean(search || dateFilter);

    return (
        <div className="space-y-4 sm:space-y-6 px-2 sm:px-0 bg-transparent min-h-screen py-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl shadow-sm border border-orange-100">
                <div>
                    <h1 className="text-lg sm:text-xl font-bold text-gray-800">Admin Login Logs</h1>
                    <p className="text-xs sm:text-sm mt-0.5 text-gray-500">
                        Track super admin login activity and session history
                    </p>
                </div>
                <button
                    onClick={() => fetchLogs(true)}
                    disabled={refreshing || loading}
                    className={`flex items-center gap-1.5 sm:gap-2 text-xs font-medium text-white px-3 sm:px-4 py-2 rounded-xl transition-all ${refreshing ? 'opacity-70' : ''}`}
                    style={{ background: 'linear-gradient(135deg, #FF7D38, #FF6B1A)', boxShadow: '0 0 16px rgba(255,125,56,0.3)' }}
                    onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 0 24px rgba(255,125,56,0.5)'}
                    onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 0 16px rgba(255,125,56,0.3)'}
                >
                    <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
                    <span className="hidden xs:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                </button>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                <Panel className="relative overflow-hidden">
                    <div
                        className="absolute -top-8 -left-8 w-24 h-24 rounded-full"
                        style={{ background: 'rgba(255,125,56,0.12)' }}
                    />
                    <div className="relative">
                        <p className="text-[10px] sm:text-xs font-medium text-gray-500">Total Logins</p>
                        <p className="text-lg sm:text-xl font-bold mt-1 text-gray-800">{logs.length}</p>
                    </div>
                </Panel>
                <Panel className="relative overflow-hidden">
                    <div
                        className="absolute -top-8 -left-8 w-24 h-24 rounded-full"
                        style={{ background: 'rgba(255,125,56,0.12)' }}
                    />
                    <div className="relative">
                        <p className="text-[10px] sm:text-xs font-medium text-gray-500">Today</p>
                        <p className="text-lg sm:text-xl font-bold mt-1 text-gray-800">{todayCount}</p>
                    </div>
                </Panel>
                <Panel className="relative overflow-hidden col-span-2 sm:col-span-1">
                    <div
                        className="absolute -top-8 -left-8 w-24 h-24 rounded-full"
                        style={{ background: 'rgba(255,125,56,0.12)' }}
                    />
                    <div className="relative">
                        <p className="text-[10px] sm:text-xs font-medium text-gray-500">Last Login</p>
                        <p className="text-sm sm:text-base font-bold mt-1 truncate text-gray-800">
                            {logs[0] ? `${formatDateLabel(logs[0].date)}, ${logs[0].time}` : '—'}
                        </p>
                    </div>
                </Panel>
            </div>

            {/* Logs panel */}
            <Panel>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div>
                        <h3 className="font-semibold text-sm sm:text-base text-gray-800">Login History</h3>
                        <p className="text-xs mt-0.5 text-gray-500">Most recent activity first</p>
                    </div>

                    <div className="flex flex-row xs:flex-row items-stretch xs:items-center gap-2 w-full sm:w-auto">
                        {/* Date filter */}
                        <div className="relative">
                            <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input
                                type="date"
                                value={dateFilter}
                                max={new Date().toISOString().split("T")[0]}
                                onChange={(e) => {
                                    setDateFilter(e.target.value);
                                    setPage(1);
                                }}
                                className="text-xs pl-8 pr-8 py-2 rounded-xl outline-none transition-all bg-orange-50/50 border border-orange-100 text-gray-700 focus:border-orange-300 focus:bg-white w-full xs:w-40"
                            />
                            {dateFilter && (
                                <button
                                    onClick={() => {
                                        setDateFilter('');
                                        setPage(1);
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-500"
                                    aria-label="Clear date filter"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>

                        {/* Search */}
                        <div className="relative w-full sm:w-64">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                placeholder="Search by date, time or admin ID"
                                className="w-full text-xs pl-8 pr-3 py-2 rounded-xl outline-none transition-all bg-orange-50/50 border border-orange-100 text-gray-700 placeholder-gray-400 focus:border-orange-300 focus:bg-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Content states */}
                {loading ? (
                    <div className="space-y-1">
                        {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
                    </div>
                ) : error ? (
                    <ErrorState message={error} onRetry={() => fetchLogs()} />
                ) : filteredLogs.length === 0 ? (
                    <EmptyState message={hasActiveFilters ? 'No matching logs found' : 'No login logs yet'} />
                ) : (
                    <>
                        <div className="divide-y divide-orange-100/50">
                            {paginatedLogs.map((log, i) => (
                                <LogRow key={log._id} log={log} isLatest={page === 1 && i === 0 && !hasActiveFilters} />
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-orange-100/70">
                                <p className="text-[10px] sm:text-xs text-gray-500">
                                    Page {page} of {totalPages} · {filteredLogs.length} entries
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed text-orange-500 bg-orange-50 border border-orange-200 hover:bg-orange-100"
                                    >
                                        <ChevronLeft size={14} />
                                    </button>
                                    <button
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed text-orange-500 bg-orange-50 border border-orange-200 hover:bg-orange-100"
                                    >
                                        <ChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Panel>
        </div>
    );
};

export default AdminLoginLogs;